import {
  DocumentProcessorBase,
  DXR,
  IProcessor,
  RichEdit,
  SubDocument,
  Table,
} from '../../../common/devexpress/RichEdit/dx-richedit';

/*
  Creates a table like below:
  -------------------------------------------------
  cells[0][0]  |  cells[0][1]  | cells[0][2]
  -------------------------------------------------
  cells[1][0]  |  cells[1][1]  | cells[1][2]
  -------------------------------------------------
  cells[2][0]  |  cells[2][1]  | cells[2][2]
  -------------------------------------------------
  ...
  We assume all `cells` rows have the same number of columns
 */
export function createTableWithCells(
  richEdit: RichEdit,
  cells: string[][],
): DocumentProcessorBase {
  const processor = richEdit.createDocumentProcessor();
  const doc = processor.document;
  if (!cells || cells.length === 0 || cells[0].length === 0) return processor;

  const table = doc.tables.create(0, cells[0].length, cells.length);

  // add data rows
  for (let rowIndex = 0; rowIndex < cells.length; rowIndex++) {
    const row = table.rows.getByIndex(rowIndex)!;
    for (let colIndex = 0; colIndex < cells[0].length; colIndex++) {
      const cell = row.cells.getByIndex(colIndex)!;
      const text = cells[rowIndex][colIndex]?.toString();
      doc.insertText(cell.interval.start, text ?? '');
    }
  }
  return processor;
}

/*
  Creates a table like below:
  -------------------------------------------------
  prop1          |  prop2          | prop3
  -------------------------------------------------
  data[0].prop1  |  data[0].prop2  | data[0].prop3
  -------------------------------------------------
  data[1].prop1  |  data[1].prop2  | data[1].prop3
  -------------------------------------------------
  data[2].prop1  |  data[2].prop2  | data[2].prop3
  -------------------------------------------------
  ...
  Each item of `data` array represents a ROW.
  Headers are added with names according to property names.

  We assume all `data` elements have the same properties!
  You could pass this list of properties as `properties` parameter,
    or pass undefined and we will just use  Object.keys(data[0])
 */
export function createTableWithDataAsRows(
  richEdit: RichEdit,
  data: Record<string, any>[],
  properties?: string[],
): DocumentProcessorBase {
  const processor = richEdit.createDocumentProcessor();
  const doc = processor.document;
  properties =
    properties ?? Object.keys(data[0]).filter((x) => x != 'toString');
  const table = doc.tables.create(0, properties.length, data.length + 1);

  const headerRow = table.rows.getByIndex(0)!;
  // add column headers
  for (let colIndex = 0; colIndex < properties.length; colIndex++) {
    const cell = headerRow.cells.getByIndex(colIndex)!;
    const text = properties[colIndex];
    doc.insertText(cell.interval.start, text ?? '');
  }

  // add data rows
  for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
    const row = table.rows.getByIndex(rowIndex + 1)!;
    for (let colIndex = 0; colIndex < properties.length; colIndex++) {
      const cell = row.cells.getByIndex(colIndex)!;
      const text = data[rowIndex][properties[colIndex]]?.toString();
      doc.insertText(cell.interval.start, text ?? '');
    }
  }
  return processor;
}

/*
  Creates a table like below:
  ---------------------------------------
  data[0][0]  |  data[1][0]  | data[2][0]
  ----------------------------------------
  data[0][1]  |  data[1][1]  | data[2][1]
  ----------------------------------------
  data[0][2]  |  data[1][2]  | data[2][2]
  ----------------------------------------
  ...
  Each item of `data` array represents a COLUMN.
  No headers are added.
  This is best if merged into another tables that are created manually in template.

 */
export function createTableWithDataAsColumns(
  richEdit: RichEdit,
  data: string[],
): DocumentProcessorBase {
  const processor = richEdit.createDocumentProcessor();
  const doc = processor.document;
  const table = doc.tables.create(0, data.length, 1);
  const row = table.rows.getByIndex(0)!;

  for (
    let colIndex = 0, cell;
    (cell = row.cells.getByIndex(colIndex));
    colIndex++
  ) {
    let text = data[colIndex]?.toString();
    if (!text) {
      text = '';
    }
    doc.insertText(cell.interval.start, text);
  }

  return processor;
}

/*
  Function finds tables that were produced by DocumentVariables and if they are within another table - attempts to merge them with a parent
 */
export function mergeTablesFromDocumentVariables(richEdit: RichEdit) {
  const doc = richEdit.document;
  for (let fieldIndex = 0; fieldIndex < doc.fields.count; fieldIndex++) {
    const field = doc.fields.getByIndex(fieldIndex);
    if (!field) continue;

    const code = doc.getText(field.codeInterval);

    const tablesContainingInterval = doc.tables.find(field.interval);
    for (
      let tableIndex = 0;
      tableIndex < tablesContainingInterval.length;
      tableIndex++
    ) {
      const table = tablesContainingInterval[tableIndex];

      // if table interval is exactly equal to a DocVariable interval, it means that table was generated by DocVariable
      if (
        table.interval.start === field.interval.start + code.length + 3 &&
        table.interval.end === field.interval.end - 1
      ) {
        const rawTable = (table as any)._table as Table;
        try {
          mergeTableIntoWrappingTable(richEdit, rawTable, code);
        } catch (e) {
          console.error('mergeTablesFromDocumentVariables', e);
        }
      }
    }
  }
}

function mergeTableIntoWrappingTable(
  richEdit: RichEdit,
  nestedTable: Table,
  code: string,
) {
  const parentCell = nestedTable.parentCell;
  if (!parentCell) {
    // it means that DocVariable's table is not wrapped by another table, do not merge
    return;
  }

  if (nestedTable.interval.start - parentCell.interval.start > 20) {
    // it means that the variable is not the only thing within a cell, which probably means that we shouldn't merge it with the wrapping table.
    // (an example of this scenario is `Bug #46167: Vertical table rewrites next lines`)
    // 20 is a difference that exists even if the variable is at the very beginning of a cell
    return;
  }
  const doc = richEdit.document;
  const parentTable = parentCell.parentRow.parentTable;
  const parentRow = parentCell.parentRow;
  const cellIndex = parentRow.cells.indexOf(parentCell);
  const rowIndex = parentTable.rows.indexOf(parentRow);
  const cellTexts = getTableCellTexts(richEdit, nestedTable);

  // delete DocVariable's table (we'll add DocVariable back at the end of a function)
  doc.deleteText(parentCell.interval);

  insertNewRowsAndCells(
    richEdit,
    parentTable,
    rowIndex + cellTexts.length,
    cellIndex + cellTexts[0].length,
  );

  // copy texts from nested table into wrapped table
  for (
    let nestedRowIndex = 0;
    nestedRowIndex < cellTexts.length;
    nestedRowIndex++
  ) {
    const nestedRow = cellTexts[nestedRowIndex];
    const wrappedTableRowIndex = rowIndex + nestedRowIndex;
    for (
      let nestedCellIndex = 0;
      nestedCellIndex < nestedRow.length;
      nestedCellIndex++
    ) {
      let cellText = nestedRow[nestedCellIndex];
      try {
        const parentCell =
          parentTable.rows[wrappedTableRowIndex].cells[
            cellIndex + nestedCellIndex
          ];
        if (!parentCell) {
          console.log(parentCell);
        }
        if (!cellText) {
          cellText = '';
        }
        // we need to clear old text for 'RefreshFieldValuesButton' scenario
        doc.deleteText(parentCell.interval);
        doc.insertText(parentCell.interval.start, cellText);
      } catch (e) {
        console.error(
          `mergeTableIntoWrappingTable: Error merging cell from row (${nestedRowIndex}), cell (${nestedCellIndex}). Cell text: '${cellText}'.`,
          e,
        );
      }
    }
  }

  doc.fields.create(parentCell.interval.start, code);
  doc.fields.showAllFieldResults(true);
}

/*
 Inserts new rows/columns if wrapping table doesn't fit
 */
function insertNewRowsAndCells(
  richEdit: RichEdit,
  table: Table,
  rows: number,
  columns: number,
) {
  const doc = richEdit.document;

  const processor: IProcessor = (doc as any)._processor;

  const subDocument: SubDocument = (doc.subDocuments.main as any)._subDocument;
  for (let rowIndex = 0; rowIndex < table.rows.length; rowIndex++) {
    const row = table.rows[rowIndex];
    while (row.cells.length < columns) {
      const lastCell = row.cells[row.cells.length - 1];

      try {
        // processor.modelManager.modelManipulator.table.insertCell(
        //   subDocument,
        //   table,
        //   rowIndex,
        //   row.cells.length - 1,
        //   lastCell,
        //   1,
        // );
        const position = new DXR.InputPositionBase();
        position.setIntervals(
          new DXR.SelectionIntervalsInfo(subDocument, [lastCell.interval]),
        );
        richEdit.selection.setSelection(lastCell.interval);
        processor.modelManager.modelManipulator.table.insertCellToTheRight(
          subDocument,
          table,
          rowIndex,
          row.cells.length - 1,
          position,
        );
      } catch (e) {
        console.log('error insertCell', e);
        throw e;
      }
    }
  }

  while (table.rows.length < rows) {
    // const rowIndex = table.rows.length - 1;
    // const intervals = table.rows[rowIndex].cells.map((x) => x.interval);
    // processor.modelManager.modelManipulator.table.insertRow(
    //   subDocument,
    //   table.index,
    //   table.rows[rowIndex],
    //   rowIndex + 1,
    //   intervals,
    // );
    const rowIndex = table.rows.length - 1;
    const position = new DXR.InputPositionBase();
    position.setIntervals(
      new DXR.SelectionIntervalsInfo(subDocument, [
        table.rows[rowIndex].interval,
      ]),
    );
    richEdit.selection.setSelection(table.rows[rowIndex].interval);
    processor.modelManager.modelManipulator.table.insertRowBelow(
      subDocument,
      table,
      rowIndex,
    );
  }
}

/*
  Returns a 2-dimensional array, containing texts of each cell of a table
 */
function getTableCellTexts(richEdit: RichEdit, nestedTable: Table): string[][] {
  const doc = richEdit.document;
  const result: string[][] = [];
  for (const nestedRow of nestedTable.rows) {
    const rowStrings: string[] = [];
    result.push(rowStrings);
    for (const nestedCell of nestedRow.cells) {
      const cellText = doc.getText(nestedCell.interval);

      rowStrings.push(cellText);
    }
  }

  return result;
}
